import React, { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import ModalDialog, {
	CloseButton,
	ModalBody,
	ModalHeader,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Flex } from '@atlaskit/primitives/compiled';

import type { SourceContentType } from '../../../types';

const i18n = defineMessages({
	copy: {
		id: 'malleable-ui-components.ephemeral-preview.copy',
		defaultMessage: 'Copy',
		description: 'Button label for copying the component URL to the clipboard',
	},
	copyLink: {
		id: 'malleable-ui-components.copy-link',
		defaultMessage: 'Copy link',
		description: 'Button label for copying the component URL to the clipboard (alternate)',
	},
	copiedToClipboard: {
		id: 'malleable-ui-components.copied-to-clipboard',
		defaultMessage: 'Copied to clipboard',
		description: 'Flag title shown after the component URL is copied to the clipboard',
	},
	insertIntoDatabase: {
		id: 'malleable-ui-components.ephemeral-preview.insert-into-database',
		defaultMessage: 'Insert into database',
		description: 'Button label for inserting the content into a database',
	},
	insertIntoWhiteboard: {
		id: 'malleable-ui-components.ephemeral-preview.insert-into-whiteboard',
		defaultMessage: 'Insert into whiteboard',
		description: 'Button label for inserting the content into a whiteboard',
	},
	insertIntoLiveDoc: {
		id: 'malleable-ui-components.ephemeral-preview.insert-into-live-doc',
		defaultMessage: 'Insert into live doc',
		description: 'Button label for inserting the content into a live doc',
	},
	insertIntoPage: {
		id: 'malleable-ui-components.ephemeral-preview.insert-into-page',
		defaultMessage: 'Insert into page',
		description: 'Button label for inserting the content into the page',
	},
});

const styles = cssMap({
	headerFlex: {
		width: '100%',
	},
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
	iframeWrapper: {
		display: 'flex',
		flex: '1',
		flexDirection: 'column',
		height: '100%',
		minHeight: '0px',
		overflow: 'hidden',
		paddingBlock: 0,
		paddingInline: 0,
		position: 'relative',
	},
});

const INSERT_BUTTON_MESSAGES: Record<SourceContentType, (typeof i18n)['insertIntoPage']> = {
	database: i18n.insertIntoDatabase,
	whiteboard: i18n.insertIntoWhiteboard,
	live: i18n.insertIntoLiveDoc,
	page: i18n.insertIntoPage,
};

/**
 * Props for the ephemeral preview modal (iframe preview with copy/insert actions).
 */
interface MalleableContentEphemeralViewProps {
	/** Called when the user closes the ephemeral preview modal. */
	onClose: () => void;
	/** Optional callback when the user clicks the insert button; receives the component URL to insert. */
	onInsertIntoPage?: (url: string) => void;
	/** URL loaded in the iframe and used for copy / insert actions. */
	iframeUrl: string;
	/**
	 * Destination for the insert button label. Maps to i18n: database, whiteboard, live, page.
	 * When not provided, defaults to "Insert into page".
	 */
	sourceContentType?: SourceContentType;
}

export function MalleableContentEphemeralView({
	onClose,
	onInsertIntoPage,
	iframeUrl,
	sourceContentType,
}: MalleableContentEphemeralViewProps): React.ReactElement {
	const [iframeSrc] = useState(iframeUrl);
	const { formatMessage } = useIntl();
	const [showCopiedFlag, setShowCopiedFlag] = useState(false);

	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(iframeUrl).then(() => {
			setShowCopiedFlag(true);
		});
	}, [iframeUrl]);

	const handleInsert = useCallback(() => {
		onInsertIntoPage?.(iframeUrl);
	}, [iframeUrl, onInsertIntoPage]);

	const insertButtonLabel = formatMessage(INSERT_BUTTON_MESSAGES[sourceContentType ?? 'page']);

	const handleDismissFlag = useCallback(() => {
		setShowCopiedFlag(false);
	}, []);

	return (
		<ModalTransition>
			<ModalDialog
				width={1200}
				height="85vh"
				onClose={onClose}
				shouldCloseOnOverlayClick
				shouldCloseOnEscapePress
				testId="malleable-content-ephemeral-view"
			>
				<ModalHeader hasCloseButton={false}>
					<Flex justifyContent="end" gap="space.100" alignItems="center" xcss={styles.headerFlex}>
						{onInsertIntoPage && (
							<Button appearance="primary" onClick={handleInsert}>
								{insertButtonLabel}
							</Button>
						)}
						<Button appearance="subtle" onClick={handleCopy}>
							{formatMessage(i18n.copy)}
						</Button>
						<CloseButton onClick={onClose} label="Close" />
					</Flex>
				</ModalHeader>
				<ModalBody hasInlinePadding={false}>
					<Box xcss={styles.iframeWrapper}>
						<Box
							as="iframe"
							xcss={styles.iframe}
							src={iframeSrc}
							title="Ephemeral Content View"
							sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
						/>
					</Box>
				</ModalBody>
			</ModalDialog>
			<FlagGroup onDismissed={handleDismissFlag}>
				{showCopiedFlag && (
					<AutoDismissFlag
						id="ephemeral-preview-copied"
						title={formatMessage(i18n.copiedToClipboard)}
						appearance="success"
						testId="ephemeral-preview-copied-flag"
					/>
				)}
			</FlagGroup>
		</ModalTransition>
	);
}
